<template>
    <kgr-loader v-if="loading" />
    <kgr-form-scaffold
        v-else
        :pending="pending"
        :pending-delete="pendingRemove"
        :edit-mode="!!ruleId"
        :with-delete-button="isDeletable"
        :with-primary-buttons="isCreatable || isEditable"
        @primaryBtnClicked="persist"
        @deleteBtnClicked="onDeleteButtonClicked"
        @backBtnClicked="redirectToOverview"
    >
        <div class="kgr-row">
            {{ $t('physicalAccess.rules.edit.intro') }}
        </div>

        <kgr-form-row
            :label="$t('Bezeichnung')"
            :error="error"
            field-name="name"
            required
        >
            <input
                v-model.trim="rule.name"
                type="text"
                class="kgr-input"
                @keyup.enter="persist"
            />
        </kgr-form-row>

        <h3 class="kgr-headline kgr-headline--h3 kgr-headline--text">
            {{ $t('Terminals') }}
        </h3>
        <table class="kgr-table kgr-table--striped kgr-table--block table-groups kgr-row">
            <thead>
                <tr class="kgr-table__row">
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('Bezeichnung') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline kgr-table__cell--actions kgr-table__cell--noGrow"></th>
                </tr>
            </thead>
            <tbody>
                <tr
                    v-for="(assignment, index) in rule.assignments"
                    :key="index"
                    class="kgr-table__row"
                >
                    <td class="kgr-table__cell">
                        {{ assignment.terminal.name }}
                    </td>
                    <td class="kgr-table__cell kgr-table__cell--actions kgr-table__cell--noGrow">
                        <kgr-icon-delete
                            class="kgr-table__action"
                            @click="removeAssignment(assignment)"
                        />
                    </td>
                </tr>
            </tbody>
            <tfoot v-if="showAddTerminalRow">
                <tr class="kgr-table__row">
                    <td class="kgr-table__cell">
                        <label class="kgr-inputGroup">
                            <div class="kgr-inputGroup__label">{{ $t('Terminal') }}</div>
                            <select
                                v-model="selectedTerminal"
                                class="kgr-select"
                            >
                                <option
                                    v-for="terminal in selectableTerminals"
                                    :value="terminal"
                                    class="kgr-select__option"
                                >{{ terminal.name }}</option>
                            </select>
                        </label>
                    </td>
                    <td class="kgr-table__cell kgr-table__cell--actions kgr-table__cell--noGrow"></td>
                </tr>
            </tfoot>
        </table>

        <div class="kgr-text kgr-text--rightAligned">
            <kgr-button
                type="grey"
                @click="addAssignment()"
            >
                {{ $t('newEntry') }}
            </kgr-button>
        </div>

        <h3 class="kgr-headline kgr-headline--h3 kgr-headline--text">
            {{ $t('Bedingungen') }}
        </h3>
        <table class="kgr-table kgr-table--striped kgr-table--block table-groups kgr-row">
            <thead>
                <tr class="kgr-table__row">
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('Bedingung') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline kgr-table__cell--actions kgr-table__cell--noGrow"></th>
                </tr>
            </thead>
            <tbody>
                <tr
                    v-for="(condition, index) in rule.conditions"
                    :key="index"
                    class="kgr-table__row"
                >
                    <td class="kgr-table__cell">
                        {{ conditionDescription(condition) }}
                    </td>
                    <td class="kgr-table__cell kgr-table__cell--actions kgr-table__cell--noGrow">
                        <kgr-icon-delete
                            class="kgr-table__action"
                            @click="removeCondition(condition)"
                        />
                    </td>
                </tr>
            </tbody>
            <tfoot v-if="showAddConditionRow">
                <tr class="kgr-table__row">
                    <td class="kgr-table__cell">
                        <div class="kgr-postfix kgr-postfix--gutters kgr-postfix--top kgr-row">
                            <div class="kgr-postfix__fluid">
                                <label class="kgr-inputGroup">
                                    <div class="kgr-inputGroup__label">{{ $t('Bedingung') }}</div>
                                    <select
                                        v-model="selectedCondition"
                                        class="kgr-select"
                                    >
                                        <option
                                            v-for="condition in selectableConditions"
                                            :key="condition.type"
                                            :value="condition.type"
                                            class="kgr-select__option"
                                        >{{ conditionName(condition) }}</option>
                                    </select>
                                </label>
                            </div>
                            <div
                                v-if="selectedCondition === 'timebetweencondition'"
                                class="kgr-postfix__fixed"
                            >
                                <kgr-time-range-picker
                                    start="00:00"
                                    end="23:55"
                                    :label="$t('Uhrzeit')"
                                    @change="selectedTimeRange = $event"
                                />
                            </div>
                            <div
                                v-if="selectedCondition === 'filterflagcondition'"
                                class="kgr-postfix__fixed"
                            >
                                <label class="kgr-inputGroup">
                                    <div class="kgr-inputGroup__label">{{ $t('Name des Auswahlkriteriums') }}</div>
                                    <kgr-member-filter-flag-select
                                        :club-id="clubId"
                                        :excluded-values="excludedFilterFlagValues"
                                        @post-input="filterFlagName = $event.value"
                                    />
                                </label>
                            </div>
                            <div
                                v-if="selectedCondition === 'classificationcondition'"
                                class="kgr-postfix__fixed"
                            >
                                <label class="kgr-inputGroup">
                                    <div class="kgr-inputGroup__label">{{ $t('Name der Zuordnung') }}</div>
                                    <kgr-member-classification-select
                                        :club-id="clubId"
                                        :excluded-values="excludedClassificationValues"
                                        @post-input="classificationName = $event.value"
                                    />
                                </label>
                            </div>
                            <div
                                v-if="selectedCondition === 'activedepartmentmembershipcondition' || selectedCondition === 'activedepartmentmembershipincludingfamilycondition'"
                                class="kgr-postfix__fixed"
                            >
                                <kgr-department-select
                                    v-model="selectedDepartment"
                                    :club-id="clubId"
                                    :excluded-ids="excludedDepartmentIds"
                                />
                            </div>
                            <div
                                v-if="selectedCondition === 'activeteammembershipcondition'"
                                class="kgr-postfix__fixed"
                            >
                                <kgr-team-select
                                    v-model="selectedTeam"
                                    :club-id="clubId"
                                    :excluded-ids="excludedTeamIds"
                                />
                            </div>
                            <div
                                v-if="selectedCondition === 'gendercondition'"
                                class="kgr-postfix__fixed"
                            >
                                <kgr-gender-select
                                    v-model="selectedGender"
                                    :excluded="excludedGenders"
                                />
                            </div>
                            <div
                                v-if="selectedCondition === 'weekdaycondition'"
                                class="kgr-postfix__fixed"
                            >
                                <kgr-weekday-multi-select
                                    v-model="selectedWeekdays"
                                />
                            </div>
                            <div
                                v-if="selectedCondition === 'clubfeecondition'"
                                class="kgr-postfix__fixed"
                            >
                                <kgr-club-fee-multi-select
                                    v-model="selectedClubFees"
                                    :club-id="clubId"
                                />
                            </div>
                        </div>
                    </td>
                    <td class="kgr-table__cell kgr-table__cell--actions kgr-table__cell--noGrow"></td>
                </tr>
            </tfoot>
        </table>

        <div class="kgr-text kgr-text--rightAligned">
            <kgr-button
                type="grey"
                @click="addCondition()"
            >
                {{ $t('newEntry') }}
            </kgr-button>
        </div>

        <h3 class="kgr-headline kgr-headline--h3 kgr-headline--text">
            {{ $t('Meldung') }}
        </h3>

        <kgr-form-row
            :label="$t('Erfolgsmeldung')"
            :tooltip="$t('physicalAccess.rule.messageTooltip')"
            :error="error"
            field-name="message"
        >
            <input
                v-model.trim="rule.message"
                type="text"
                class="kgr-input"
                :placeholder="$t('Viel Spaß, [first] [last]!')"
                @keyup.enter="persist"
            />
        </kgr-form-row>
    </kgr-form-scaffold>
</template>

<script>
import ClubFeeMultiSelect from '../../components/common/form/select/ClubFeeMultiSelect.vue';
import DepartmentSelect from '../../components/common/form/select/DepartmentSelect';
import Error from '../../js/model/Error';
import FormRow from '../../components/common/form/FormRow';
import FormScaffold from '../../components/common/form/FormScaffold';
import GenderSelect from '../../components/common/form/select/GenderSelect';
import IconDelete from '../../components/common/icon/IconDelete';
import KgrButton from '../../components/common/form/button/KgrButton';
import Loading from '../../components/common/Loading';
import MemberClassifcationSelect from '../../components/members/MemberClassifcationSelect';
import MemberFilterFlagSelect from '../../components/members/MemberFilterFlagSelect';
import PhysicalAccessConditionRepository from '../../js/repository/PhysicalAccessConditionRepository';
import PhysicalAccessRuleRepository from '../../js/repository/PhysicalAccessRuleRepository';
import PhysicalAccessTerminalRepository from '../../js/repository/PhysicalAccessTerminalRepository';
import TeamSelect from '../../components/common/form/select/TeamSelect';
import TimeRangePicker from '../../components/common/form/picker/TimeRangePicker';
import WeekdayMultiSelect from '../../components/common/form/select/WeekdayMultiSelect';

export default {
    components: {
        'kgr-club-fee-multi-select': ClubFeeMultiSelect,
        'kgr-department-select': DepartmentSelect,
        'kgr-team-select': TeamSelect,
        'kgr-member-filter-flag-select': MemberFilterFlagSelect,
        'kgr-member-classification-select': MemberClassifcationSelect,
        'kgr-gender-select': GenderSelect,
        'kgr-weekday-multi-select': WeekdayMultiSelect,
        'kgr-loader': Loading,
        'kgr-form-row': FormRow,
        'kgr-button': KgrButton,
        'kgr-icon-delete': IconDelete,
        'kgr-time-range-picker': TimeRangePicker,
        'kgr-form-scaffold': FormScaffold,
    },
    props: {
        clubId: {
            type: Number,
            required: true,
        },
        ruleId: {
            type: String,
            required: false,
        },
        overviewUrl: {
            type: String,
            required: true,
        },
        isCreatable: {
            type: Boolean,
            required: false,
            default: false,
        },
        isEditable: {
            type: Boolean,
            required: false,
            default: false,
        },
        isDeletable: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    data() {
        return {
            loading: false,
            pending: false,
            pendingRemove: false,
            error: new Error(),
            rule: {
                name: '',
                assignments: [],
                conditions: [],
                message: null,
            },
            terminals: [],
            conditions: [],
            showAddTerminalRow: false,
            showAddConditionRow: false,
            selectedTerminal: null,
            selectedCondition: null,
            selectedTimeRange: null,
            selectedDepartment: null,
            selectedTeam: null,
            selectedGender: null,
            selectedWeekdays: [],
            selectedClubFees: [],
            filterFlagName: '',
            classificationName: '',
        }
    },
    computed: {
        selectableTerminals() {
            return this.terminals.filter(terminal => !this.rule.assignments.map(assignment => assignment.terminal.id).includes(terminal.id));
        },
        selectableConditions() {
            return this.conditions.filter(condition => {
                const conditionsWithoutParameters = ['contractstatusactivecondition','contractstatusactiveasguestcondition','contractstatuspassivecondition', 'contractstatusactiveincludingfamilycondition', 'ispresentcondition', 'isnotpresentcondition', 'ispublicholidaycondition', 'isnotpublicholidaycondition'];

                if (conditionsWithoutParameters.includes(condition.type) && this.rule.conditions.find(assignedCondition => assignedCondition.type === condition.type) !== undefined) {
                    return false;
                }

                return true;
            });
        },
        excludedGenders() {
            if (!this.rule || !this.selectedCondition) {
                return [];
            }

            return this.rule.conditions
                .filter(condition => condition.type === this.selectedCondition)
                .map(condition => condition.gender);
        },
        excludedDepartmentIds() {
            if (!this.rule || !this.selectedCondition) {
                return [];
            }

            return this.rule.conditions
                .filter(condition => condition.type === this.selectedCondition)
                .map(condition => condition.departmentId);
        },
        excludedTeamIds() {
            if (!this.rule || !this.selectedCondition) {
                return [];
            }

            return this.rule.conditions
                .filter(condition => condition.type === this.selectedCondition)
                .map(condition => condition.teamId);
        },
        excludedFilterFlagValues() {
            if (!this.rule) {
                return [];
            }

            return this.rule.conditions
                .filter(condition => condition.type === 'filterflagcondition')
                .map(condition => condition.filterFlagName);
        },
        excludedClassificationValues() {
            if (!this.rule) {
                return [];
            }

            return this.rule.conditions
                .filter(condition => condition.type === 'classificationcondition')
                .map(condition => condition.classificationName);
        },
    },
    created() {
        if (this.ruleId) {
            this.load();
        }

        PhysicalAccessTerminalRepository
            .findByClub(this.clubId)
            .then(terminals => this.terminals = terminals);
        PhysicalAccessConditionRepository
            .findAll()
            .then(conditions => this.conditions = conditions);
    },
    methods: {
        redirectToOverview() {
            window.location = this.overviewUrl;
        },
        persist() {
            if (this.pending) {
                return;
            }

            this.pending = true;

            if (this.showAddTerminalRow) {
                this.addAssignment();
            }

            if (this.showAddConditionRow) {
                this.addCondition();
            }

            const persistPromise = this.ruleId ?
                PhysicalAccessRuleRepository.update(this.rule.id, this.rule) : PhysicalAccessRuleRepository.create(this.clubId, this.rule);

            persistPromise
                .then(() => {
                    this.$notify.success('saveChanges.success', {}, { saveInSession: true });
                    window.location = this.overviewUrl;
                })
                .catch(error => {
                    this.handleError(error);
                    this.pending = false;
                });
        },
        onDeleteButtonClicked() {
            showConfirm(this.$t('confirm'), this.$t('physicalAccess.rule.delete.confirm', {'name': this.rule.name}), () => {
                this.pendingRemove = true;
                PhysicalAccessRuleRepository.remove(this.rule.id)
                    .then(() => {
                        this.error = new Error();
                        this.$notify.success('Zutrittsregel erfolgreich gelöscht', {}, { saveInSession: true });
                        this.redirectToOverview();
                    })
                    .catch(error => {
                        this.handleError(error);
                        this.pendingRemove = false
                    });
            }, 'Ok');
        },
        handleError(error) {
            this.error = error;
            this.$notify.error('saveChanges.error');
        },
        load() {
            this.loading = true;
            PhysicalAccessRuleRepository
                .find(this.ruleId)
                .then(rule => {
                    this.rule = rule;
                    this.loading = false;
                });
        },
        addAssignment() {
            if (this.showAddTerminalRow && this.selectedTerminal) {
                this.rule.assignments.push(
                    {
                        id: null ,
                        terminal: this.selectedTerminal,
                    }
                );
                this.selectedTerminal = null;
            } else {
                this.showAddTerminalRow = true;
            }
        },
        removeAssignment(assignmentToRemove) {
            this.rule.assignments = this.rule.assignments.filter(assignment => assignment !== assignmentToRemove);
        },
        addCondition() {
            if (this.showAddConditionRow && this.selectedCondition) {
                const params = {
                    id: null,
                    type: this.selectedCondition
                };

                if (this.selectedCondition === 'timebetweencondition') {
                    if (!this.selectedTimeRange) {
                        return;
                    }

                    params.timeRange = this.selectedTimeRange;
                }
                if (this.selectedCondition === 'filterflagcondition') {
                    if (!this.filterFlagName) {
                        return;
                    }

                    params.filterFlagName = this.filterFlagName;
                }
                if (this.selectedCondition === 'classificationcondition') {
                    if (!this.classificationName) {
                        return;
                    }

                    params.classificationName = this.classificationName;
                }
                if (this.selectedCondition === 'activedepartmentmembershipcondition' || this.selectedCondition === 'activedepartmentmembershipincludingfamilycondition') {
                    if (!this.selectedDepartment) {
                        return;
                    }

                    params.departmentId = this.selectedDepartment.id;
                    params.departmentName = this.selectedDepartment.name;
                    if (this.selectedDepartment.hierarchyPath) {
                        params.departmentName = this.selectedDepartment.hierarchyPath + ' / ' + params.departmentName;
                    }
                }
                if (this.selectedCondition === 'activeteammembershipcondition') {
                    if (!this.selectedTeam) {
                        return;
                    }

                    params.teamId = this.selectedTeam.id;
                    params.teamName = this.selectedTeam.name;
                    params.teamLongName = this.selectedTeam.name + ' (' + this.selectedTeam.departmentName + ')';
                }
                if (this.selectedCondition === 'gendercondition') {
                    if (!this.selectedGender) {
                        return;
                    }

                    params.gender = this.selectedGender;
                }
                if (this.selectedCondition === 'weekdaycondition') {
                    if (!this.selectedWeekdays || this.selectedWeekdays.length === 0) {
                        return;
                    }

                    params.weekdays = this.selectedWeekdays;
                }
                if (this.selectedCondition === 'clubfeecondition') {
                    if (!this.selectedClubFees || this.selectedClubFees.length === 0) {
                        return;
                    }

                    params.clubFees = this.selectedClubFees.map(clubFee => clubFee.id);
                    params.clubFeeNames = this.selectedClubFees.map(clubFee => clubFee.name);
                }
                this.rule.conditions.push(params);
                this.selectedCondition = null;
                this.selectedDepartment = null;
                this.selectedTeam = null;
                this.filterFlagName = null;
                this.classificationName = null;
                this.selectedTimeRange = null;
                this.selectedGender = null;
                this.selectedWeekdays = [];
                this.selectedClubFees = [];
            } else {
                this.showAddConditionRow = true;
            }
        },
        removeCondition(conditionToRemove) {
            this.rule.conditions = this.rule.conditions.filter(condition => condition !== conditionToRemove);
        },
        conditionName(condition) {
            switch (condition.type) {
                case 'activedepartmentmembershipcondition':
                    return this.$t('Mitgliedschaft in Abteilung');
                case 'activeteammembershipcondition':
                    return this.$t('Mitgliedschaft in Team');
                case 'activedepartmentmembershipincludingfamilycondition':
                    return this.$t('Eigene Mitgliedschaft oder die eines Familienmitglieds in Abteilung');
                case 'contractstatusactivecondition':
                    return this.$t('Aktive Vereinsmitgliedschaft');
                case 'contractstatusactiveasguestcondition':
                    return this.$t('Gastmitgliedschaft');
                case 'contractstatuspassivecondition':
                    return this.$t('Passive Vereinsmitgliedschaft');
                case 'contractstatusactiveincludingfamilycondition':
                    return this.$t('Eigene aktive Vereinsmitgliedschaft oder die eines Familienmitglieds');
                case 'filterflagcondition':
                    return this.$t('Vorhandenes Auswahlkriterium');
                case 'classificationcondition':
                    return this.$t('Vorhandene Zuordnung');
                case 'gendercondition':
                    return this.$t('Geschlecht');
                case 'timebetweencondition':
                    return this.$t('Uhrzeit liegt zwischen');
                case 'weekdaycondition':
                    return this.$t('Wochentag ist');
                case 'ispresentcondition':
                    return this.$t('Ist anwesend');
                case 'isnotpresentcondition':
                    return this.$t('Ist nicht anwesend');
                case 'ispublicholidaycondition':
                    return this.$t('Feiertag');
                case 'isnotpublicholidaycondition':
                    return this.$t('Kein Feiertag');
                case 'clubfeecondition':
                    return this.$t('Hat eine der Gebühren');
            }
            throw new Error('Invalid condition type: ' + condition.type);
        },
        conditionDescription(condition) {
            switch (condition.type) {
                case 'activedepartmentmembershipcondition':
                    return this.$t('physicalAccess.condition.activedepartmentmembership.description', { departmentName: condition.departmentName });
                case 'activeteammembershipcondition':
                    return this.$t('Mitgliedschaft in Team: {teamName}', { teamName: condition.teamLongName });
                case 'activedepartmentmembershipincludingfamilycondition':
                    return this.$t('physicalAccess.condition.activedepartmentmembershipincludingfamily.description', { departmentName: condition.departmentName });
                case 'contractstatusactivecondition':
                    return this.$t('Aktive Vereinsmitgliedschaft');
                case 'contractstatusactiveasguestcondition':
                    return this.$t('Gastmitgliedschaft');
                case 'contractstatuspassivecondition':
                    return this.$t('Passive Vereinsmitgliedschaft');
                case 'contractstatusactiveincludingfamilycondition':
                    return this.$t('Eigene aktive Vereinsmitgliedschaft oder die eines Familienmitglieds');
                case 'filterflagcondition':
                    return this.$t('physicalAccess.condition.filterflag.description', { filterFlagName: condition.filterFlagName });
                case 'classificationcondition':
                    return this.$t('physicalAccess.condition.classification.description', { classificationName: condition.classificationName });
                case 'gendercondition':
                    return this.$t('physicalAccess.condition.gender.description', { genderName: this.$t(condition.gender) });
                case 'weekdaycondition':
                    return this.$t('Wochentag ist {weekdays}', { weekdays: this.translateWeekdays(condition.weekdays) });
                case 'timebetweencondition': {
                    const start = typeof condition.timeRange.start === 'object' ? condition.timeRange.start.toString() : condition.timeRange.start;
                    const end = typeof condition.timeRange.end === 'object' ? condition.timeRange.end.toString() : condition.timeRange.end;
                    return this.$t('Uhrzeit liegt zwischen {start} und {end}', { start, end });
                }
                case 'ispresentcondition':
                    return this.$t('Ist anwesend');
                case 'isnotpresentcondition':
                    return this.$t('Ist nicht anwesend');
                case 'ispublicholidaycondition':
                    return this.$t('Feiertag');
                case 'isnotpublicholidaycondition':
                    return this.$t('Kein Feiertag');
                case 'clubfeecondition':
                    return this.$t('Hat eine der Gebühren: {clubFeeNames}', { clubFeeNames: condition.clubFeeNames.join(', ') });
            }

            throw new Error('Invalid condition type: ' + condition.type);
        },
        translateWeekdays(weekdays) {
            let weekdayString = this.$t('weekday_' + weekdays[0].value);
            for (let i = 1; i < weekdays.length; i++) {
                weekdayString += ', ' + this.$t('weekday_' + weekdays[i].value);
            }
            return weekdayString;
        },
    },
}
</script>
